/**
 * Clear AI upload data when variant changes
 *
 * When user switches variants (Model/Color), clear the uploaded image preview
 * to prompt them to re-upload or re-generate for the new variant.
 */

(function() {
  'use strict';

  // Track if cart submission is in progress
  let isSubmittingToCart = false;

  // Store snapshot of form data before submission
  let formDataSnapshot = null;

  /**
   * Clear AI upload data for a section
   * @param {string} sectionId - The section ID
   */
  function clearUploadData(sectionId) {
    const container = document.querySelector(`#CasecaseAiUpload-${sectionId}`);
    if (!container) return;

    const imageUrlInput = container.querySelector('[data-ai-generated-image-input]');
    const displayUrlInput = container.querySelector('[data-ai-generated-display-input]');
    const jobIdInput = container.querySelector('[data-ai-generated-job-id-input]');
    const previewImg = container.querySelector('.ai-upload-preview');
    const uploadButton = container.querySelector('.ai-upload-button');
    const fileInput = container.querySelector('[data-ai-upload-input]');

    if (imageUrlInput) imageUrlInput.value = '';
    if (displayUrlInput) displayUrlInput.value = '';
    if (jobIdInput) jobIdInput.value = '';
    if (fileInput) fileInput.value = '';
    if (previewImg) previewImg.removeAttribute('src');
    if (uploadButton) uploadButton.classList.remove('has-image', 'validation-error');
  }

  /**
   * Create a snapshot of all AI upload data
   */
  function createFormSnapshot() {
    const snapshot = {};
    document.querySelectorAll('[data-casecase-ai-upload]').forEach((container) => {
      const sectionId = container.dataset.sectionId;
      if (!sectionId) return;

      const imageUrl = container.querySelector('[data-ai-generated-image-input]')?.value || '';
      const displayUrl = container.querySelector('[data-ai-generated-display-input]')?.value || '';
      const jobId = container.querySelector('[data-ai-generated-job-id-input]')?.value || '';

      if (imageUrl || displayUrl) {
        snapshot[sectionId] = { imageUrl, displayUrl, jobId };
      }
    });
    return snapshot;
  }

  /**
   * Restore form data from snapshot
   */
  function restoreFromSnapshot() {
    if (!formDataSnapshot) return;

    for (const [sectionId, data] of Object.entries(formDataSnapshot)) {
      const container = document.querySelector(`#CasecaseAiUpload-${sectionId}`);
      if (!container) continue;

      const imageUrlInput = container.querySelector('[data-ai-generated-image-input]');
      const displayUrlInput = container.querySelector('[data-ai-generated-display-input]');
      const jobIdInput = container.querySelector('[data-ai-generated-job-id-input]');

      if (imageUrlInput && data.imageUrl) imageUrlInput.value = data.imageUrl;
      if (displayUrlInput && data.displayUrl) displayUrlInput.value = data.displayUrl;
      if (jobIdInput && data.jobId) jobIdInput.value = data.jobId;
    }
  }

  /**
   * Check if a radio input belongs to the "Model" option
   * @param {HTMLInputElement} input
   * @returns {boolean}
   */
  function isModelOption(input) {
    const name = input.name || '';
    return /^model[-_]/i.test(name) || /^iphone[-_\s]/i.test(name);
  }

  /**
   * Disable Color fieldset and Add to Cart button temporarily
   * @param {number} duration - Duration in milliseconds
   */
  function disableUITemporarily(duration = 500) {
    const fieldsets = document.querySelectorAll('fieldset.variant-option');
    fieldsets.forEach((fieldset) => {
      const legend = fieldset.querySelector('legend');
      if (legend && /^color$/i.test(legend.textContent.trim())) {
        fieldset.disabled = true;
        fieldset.style.opacity = '0.5';
        fieldset.style.pointerEvents = 'none';
        fieldset.dataset.tempDisabled = 'true';

        setTimeout(() => {
          if (fieldset.dataset.tempDisabled === 'true') {
            fieldset.disabled = false;
            fieldset.style.opacity = '';
            fieldset.style.pointerEvents = '';
            delete fieldset.dataset.tempDisabled;
          }
        }, duration);
      }
    });

    const addToCartButtons = document.querySelectorAll('[ref="addToCartButton"]');
    addToCartButtons.forEach((button) => {
      if (!button.disabled) {
        button.disabled = true;
        button.dataset.tempDisabled = 'true';

        setTimeout(() => {
          if (button.dataset.tempDisabled === 'true') {
            button.disabled = false;
            delete button.dataset.tempDisabled;
          }
        }, duration);
      }
    });
  }

  /**
   * Lock all form inputs during cart submission
   */
  function lockFormOnAddToCart() {
    let isLocked = false;
    let lockTimeout = null;
    const lockedElements = [];

    function lockAllInputs() {
      if (isLocked) return;
      isLocked = true;
      isSubmittingToCart = true;
      lockedElements.length = 0;

      const variantInputs = document.querySelectorAll('variant-picker input[type="radio"], variant-picker select');
      variantInputs.forEach((input) => {
        if (!input.disabled) {
          input.disabled = true;
          input.dataset.lockedByCart = 'true';
          lockedElements.push(input);
        }
      });

      const fieldsets = document.querySelectorAll('variant-picker fieldset');
      fieldsets.forEach((fieldset) => {
        if (!fieldset.disabled) {
          fieldset.disabled = true;
          fieldset.style.opacity = '0.6';
          fieldset.style.pointerEvents = 'none';
          fieldset.dataset.lockedByCart = 'true';
          lockedElements.push(fieldset);
        }
      });

      const uploadButtons = document.querySelectorAll('[data-ai-upload-trigger]');
      uploadButtons.forEach((button) => {
        if (!button.disabled) {
          button.disabled = true;
          button.style.opacity = '0.6';
          button.style.pointerEvents = 'none';
          button.dataset.lockedByCart = 'true';
          lockedElements.push(button);
        }
      });

      const addToCartButtons = document.querySelectorAll('[ref="addToCartButton"]');
      addToCartButtons.forEach((button) => {
        if (!button.disabled) {
          button.disabled = true;
          button.dataset.lockedByCart = 'true';
          lockedElements.push(button);
        }
      });

      // Safety timeout: 5 seconds
      lockTimeout = setTimeout(() => {
        console.warn('[preserve-ai-uploads] Cart drawer did not open within 5s, auto-unlocking');
        unlockAllInputs();
      }, 5000);
    }

    function unlockAllInputs() {
      if (!isLocked) return;
      isLocked = false;
      isSubmittingToCart = false;
      formDataSnapshot = null;

      if (lockTimeout) {
        clearTimeout(lockTimeout);
        lockTimeout = null;
      }

      lockedElements.forEach((element) => {
        if (element.dataset.lockedByCart === 'true') {
          element.disabled = false;
          element.style.opacity = '';
          element.style.pointerEvents = '';
          delete element.dataset.lockedByCart;
        }
      });

      lockedElements.length = 0;
    }

    // Intercept form submission to create snapshot BEFORE anything happens
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && form.matches('[data-type="add-to-cart-form"]')) {
        // Create snapshot immediately
        formDataSnapshot = createFormSnapshot();

        // Lock UI after a tiny delay
        setTimeout(() => {
          lockAllInputs();
        }, 10);
      }
    }, true); // Use capture phase

    // Watch for cart drawer/notification opening
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement) {
            if (node.matches('cart-drawer[open], cart-notification[active], .cart-drawer[open]') ||
                node.querySelector('cart-drawer[open], cart-notification[active], .cart-drawer[open]')) {
              unlockAllInputs();
              return;
            }
          }
        }

        if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
          const target = mutation.target;
          if (target.matches('cart-drawer, cart-notification, .cart-drawer')) {
            if (target.hasAttribute('open') || target.hasAttribute('active')) {
              unlockAllInputs();
              return;
            }
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'active']
    });

    document.addEventListener('cart:add', () => {
      setTimeout(() => {
        unlockAllInputs();
      }, 200);
    });

    document.addEventListener('cart:update', () => {
      setTimeout(() => {
        unlockAllInputs();
      }, 200);
    });
  }

  // Initialize form locking on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lockFormOnAddToCart);
  } else {
    lockFormOnAddToCart();
  }

  // Initialize form locking on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lockFormOnAddToCart);
  } else {
    lockFormOnAddToCart();
  }

  // Block variant changes during cart submission
  document.addEventListener('change', (event) => {
    const input = event.target;

    // If cart submission is in progress, prevent variant changes and restore snapshot
    if (isSubmittingToCart && input instanceof HTMLInputElement &&
        (input.type === 'radio' || input.tagName === 'SELECT') &&
        input.closest('variant-picker')) {
      event.preventDefault();
      event.stopImmediatePropagation();

      // Force restore from snapshot
      if (formDataSnapshot) {
        setTimeout(() => restoreFromSnapshot(), 0);
      }
      return false;
    }

    // Handle Model option switching
    if (input instanceof HTMLInputElement && input.type === 'radio' && isModelOption(input)) {
      disableUITemporarily(500);
    }
  }, true);

  // Block variant changes during submission
  document.addEventListener('variant:selected', (event) => {
    // Block if cart submission is in progress
    if (isSubmittingToCart) {
      event.preventDefault();
      event.stopImmediatePropagation();

      // Force restore from snapshot
      if (formDataSnapshot) {
        setTimeout(() => restoreFromSnapshot(), 0);
      }
      return false;
    }
  }, true);

  // Clear upload data after variant change completes
  document.addEventListener('variant:update', () => {
    // If submitting to cart, force restore from snapshot instead of clearing
    if (isSubmittingToCart && formDataSnapshot) {
      setTimeout(() => restoreFromSnapshot(), 0);
      return;
    }

    // Clear upload data for all AI upload containers
    setTimeout(() => {
      document.querySelectorAll('[data-casecase-ai-upload]').forEach((container) => {
        if (container.dataset.sectionId) {
          clearUploadData(container.dataset.sectionId);
        }
      });
    }, 100);
  });
})();

